
import React from 'react';
import { Button } from "@/components/ui/button";
import { cn } from '@/lib/utils';

interface PageHeroProps {
  badge?: string;
  title: string;
  description?: string;
  className?: string;
  buttons?: Array<{
    text: string;
    variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link" | "blue" | "consultation" | "demo" | null | undefined;
    href?: string;
    onClick?: () => void;
    component?: React.ReactNode;
  }>;
}

const PageHero = ({ badge, title, description, className, buttons }: PageHeroProps) => {
  return (
    <section className={cn(
      "pt-32 pb-20 px-6 md:px-10 bg-gray-900 text-white relative overflow-hidden",
      className
    )}>
      {/* Enhanced Background Animation */}
      <div className="absolute inset-0 bg-[#010b20] z-0 overflow-hidden">
        {/* Animated wave element */}
        <div className="absolute w-full h-full overflow-hidden">
          <div className="absolute top-1/2 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-500/30 to-transparent transform -translate-y-1/2 animate-wave"></div>
          <div className="absolute top-[45%] left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-400/20 to-transparent transform -translate-y-1/2 animate-wave-slow"></div>
          <div className="absolute top-[55%] left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-300/20 to-transparent transform -translate-y-1/2 animate-wave-slower"></div>
          
          {/* Glowing point that emits the waves */}
          <div className="absolute top-1/2 left-[20%] w-2 h-2 rounded-full bg-white/90 shadow-[0_0_30px_15px_rgba(59,130,246,0.5)] animate-pulse-gentle transform -translate-y-1/2"></div>
          
          {/* Radial rays emanating from the glowing point */}
          <div className="absolute top-1/2 left-[20%] w-20 h-20 animate-rays-rotate transform -translate-x-1/2 -translate-y-1/2">
            {Array.from({ length: 12 }).map((_, index) => (
              <div 
                key={index} 
                className="absolute top-1/2 left-1/2 h-[1px] w-10 bg-gradient-to-r from-blue-400/90 to-transparent origin-left"
                style={{ transform: `rotate(${index * 30}deg)` }}
              ></div>
            ))}
          </div>
        </div>
        
        {/* Ambient glow overlays */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-blue-900/30 to-purple-900/20"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-500/10 via-transparent to-transparent blur-3xl"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-purple-500/10 via-transparent to-transparent blur-3xl"></div>
      </div>
      
      <div className="container mx-auto text-center relative z-10">
        {badge && (
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-primary/20 text-primary mb-4 animate-fade-in">
            <span className="text-sm font-medium">{badge}</span>
          </div>
        )}
        <h1 className="text-4xl md:text-5xl font-bold mb-6 animate-fade-in">{title}</h1>
        {description && (
          <p className="text-xl text-white/80 max-w-3xl mx-auto mb-10 animate-fade-in">
            {description}
          </p>
        )}
        {buttons && buttons.length > 0 && (
          <div className="flex flex-wrap justify-center gap-4 animate-fade-in delay-400">
            {buttons.map((button, index) => (
              button.component ? (
                <React.Fragment key={index}>
                  {button.component}
                </React.Fragment>
              ) : (
                <Button 
                  key={index}
                  variant={button.variant} 
                  size="lg" 
                  className={button.variant === "outline" 
                    ? "bg-transparent border-white/20 text-white hover:bg-white/10" 
                    : "btn-hover-effect group"
                  }
                  onClick={button.onClick}
                  asChild={!!button.href}
                >
                  {button.href ? (
                    <a href={button.href}>{button.text}</a>
                  ) : (
                    button.text
                  )}
                </Button>
              )
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default PageHero;
